import { For, Show, createMemo, type Component } from 'solid-js';
import type { Spread } from '~/domain/types';
import type { AddCheck } from '~/domain/strategy';
import { analyseSpread } from '~/domain/spread';
import WheelThumbnail from '~/components/WheelThumbnail';

/**
 * The spread catalogue (SPEC-V2 §4.3–§4.4) — one list holding both pre-built
 * and user-made spreads, doing double duty as a browser (open in the editor)
 * and, when `checkAdd` is supplied, a picker for the strategy composer (add as
 * a layer). Filtering by wheel style is the caller's job — this component
 * only sorts and renders whatever `entries` it is given.
 *
 * Every figure shown is recomputed here via `analyseSpread` rather than read
 * off the spread record, so a row can never claim coverage or a stake the
 * bets themselves do not produce (CLAUDE.md invariant 1).
 */

export interface CatalogueEntry {
  readonly spread: Spread;
  /** Pre-built spreads are reference data — never edited or deleted here. */
  readonly builtIn: boolean;
}

export interface SpreadCatalogueProps {
  entries: readonly CatalogueEntry[];
  /**
   * Set while composing a strategy: says whether each row can join and why not.
   * Absent means the catalogue is being browsed rather than composed from.
   */
  checkAdd?: (spread: Spread) => AddCheck;
  /** Click. Opens the spread when browsing; adds a layer when composing. */
  onActivate: (entry: CatalogueEntry) => void;
  /** Hover, for the wheel/felt preview. Null on leave. */
  onPreview: (entry: CatalogueEntry | null) => void;
  /** Marks the spread currently open in the editor, if any. */
  activeSpreadId?: string | null;
  onDelete?: (spread: Spread) => void;
  class?: string;
}

/** Built-in rows first, then user-made — each group alphabetical by name, so
 *  the split stays legible instead of interleaving reference and personal work. */
const sortEntries = (entries: readonly CatalogueEntry[]): CatalogueEntry[] =>
  [...entries].sort((a, b) => {
    if (a.builtIn !== b.builtIn) return a.builtIn ? -1 : 1;
    return a.spread.name.localeCompare(b.spread.name);
  });

const SpreadCatalogue: Component<SpreadCatalogueProps> = (props) => {
  const sorted = createMemo(() => sortEntries(props.entries));

  return (
    <div class={`space-y-1 ${props.class ?? ''}`}>
      <Show
        when={sorted().length > 0}
        fallback={<p class="px-1 py-2 text-[10px] text-ink-faint">No spreads to show.</p>}
      >
        <For each={sorted()}>
          {(entry) => {
            const analysis = createMemo(() =>
              analyseSpread(entry.spread.bets, entry.spread.wheelStyle),
            );
            // Calling checkAdd here (rather than hoisting it) keeps the read
            // inside this row's own memo, so only rows whose verdict actually
            // changes re-render when the strategy underneath it changes.
            const verdict = createMemo<AddCheck | null>(() =>
              props.checkAdd ? props.checkAdd(entry.spread) : null,
            );
            const blocked = createMemo(() => verdict()?.ok === false);
            const reasonText = createMemo(() => {
              const v = verdict();
              return v && !v.ok ? v.reason : '';
            });
            const isActive = createMemo(() => entry.spread.id === props.activeSpreadId);
            const canDelete = createMemo(() => !entry.builtIn && props.onDelete !== undefined);

            const handleClick = () => {
              if (blocked()) return;
              props.onActivate(entry);
            };

            const handleDelete = (event: MouseEvent) => {
              event.stopPropagation();
              if (window.confirm(`Delete "${entry.spread.name}"? This cannot be undone.`)) {
                props.onDelete?.(entry.spread);
              }
            };

            return (
              <div
                class="group flex items-start gap-2 rounded border px-2 py-1.5 transition-colors"
                classList={{
                  'cursor-pointer': !blocked(),
                  'opacity-40': blocked(),
                  'border-placed bg-placed/15': isActive() && !blocked(),
                  'border-line bg-surface-2 hover:border-line-strong': !isActive() && !blocked(),
                  'border-line bg-surface-2': blocked(),
                }}
                onClick={handleClick}
                onMouseEnter={() => props.onPreview(entry)}
                onMouseLeave={() => props.onPreview(null)}
              >
                <WheelThumbnail
                  bets={entry.spread.bets}
                  wheelStyle={entry.spread.wheelStyle}
                  size={28}
                  class="shrink-0"
                />

                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-2">
                    <span class="truncate text-[11px] font-medium text-ink">
                      {entry.spread.name}
                    </span>
                    <Show when={entry.builtIn}>
                      <span class="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-ink-faint">
                        built-in
                      </span>
                    </Show>
                  </div>

                  <Show
                    when={!blocked()}
                    fallback={
                      <p class="mt-1 text-[10px] leading-relaxed text-ink-faint">
                        {reasonText()}
                      </p>
                    }
                  >
                    <div class="mt-1 flex items-baseline justify-between gap-2">
                      <span class="tnum flex items-baseline gap-2 text-[10px] text-ink-faint">
                        <span>{analysis().pocketsCovered} pockets</span>
                        <span>·</span>
                        <span>{analysis().totalStake} chips</span>
                      </span>
                      {/* The longest bare run is what the catalogue is conceptually
                          sorted around (SPEC-V2 §4.3), so it gets its own emphasised
                          slot rather than sitting inline with the other figures. */}
                      <span class="tnum flex shrink-0 items-baseline gap-1">
                        <span class="text-[15px] font-semibold leading-none text-ink">
                          {analysis().longestGap?.length ?? 0}
                        </span>
                        <span class="text-[9px] uppercase tracking-[0.06em] text-ink-faint">
                          bare run
                        </span>
                      </span>
                    </div>
                  </Show>
                </div>

                <Show when={canDelete()}>
                  <button
                    type="button"
                    onClick={handleDelete}
                    title="Delete this spread"
                    class="shrink-0 self-start rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-faint hover:border-loss hover:text-loss"
                  >
                    Delete
                  </button>
                </Show>
              </div>
            );
          }}
        </For>
      </Show>
    </div>
  );
};

export default SpreadCatalogue;
